import type {
  MeetingTemplateCreateRequest,
  MeetingTemplateDetail,
  MeetingTemplateItem,
  MeetingTemplateListPurpose,
  MeetingTemplateScope,
  MeetingTemplateUpdateRequest,
} from '@wundo/shared/meetingTemplate.ts';
import { apiFetch, apiFetchJson } from './api.ts';

export type {
  MeetingTemplateCreateRequest,
  MeetingTemplateDetail,
  MeetingTemplateItem,
  MeetingTemplateUpdateRequest,
};

const collection = (userId: string) => `/users/${userId}/meeting-templates`;

/**
 * The organisation's gesprekstypes plus this user's own. A picker asks to
 * `use` them; the settings screen asks to `manage` them, where an admin also
 * sees the ones aimed at other cohorts.
 */
export async function fetchMeetingTemplates(
  userId: string,
  purpose: MeetingTemplateListPurpose,
): Promise<MeetingTemplateItem[]> {
  const { data } = await apiFetchJson<{ data: MeetingTemplateItem[] }>(
    `${collection(userId)}?${new URLSearchParams({ purpose })}`,
  );
  return data;
}

/** One gesprekstype in full, for the screen that edits it. */
export async function fetchMeetingTemplate(
  userId: string,
  templateId: string,
): Promise<MeetingTemplateDetail> {
  return apiFetchJson<MeetingTemplateDetail>(`${collection(userId)}/${templateId}`);
}

export async function createMeetingTemplate(
  userId: string,
  template: MeetingTemplateCreateRequest,
): Promise<MeetingTemplateDetail> {
  return apiFetchJson<MeetingTemplateDetail>(collection(userId), {
    method: 'POST',
    body: JSON.stringify(template),
  });
}

/** Save the whole template: it is one form and it is stored as one record. */
export async function saveMeetingTemplate(
  userId: string,
  templateId: string,
  template: MeetingTemplateUpdateRequest,
): Promise<MeetingTemplateDetail> {
  return apiFetchJson<MeetingTemplateDetail>(`${collection(userId)}/${templateId}`, {
    method: 'PUT',
    body: JSON.stringify(template),
  });
}

export async function deleteMeetingTemplate(
  userId: string,
  templateId: string,
): Promise<void> {
  await apiFetch(`${collection(userId)}/${templateId}`, { method: 'DELETE' });
}

/**
 * The order one of the two lists is read in, as the whole list rather than as
 * a move.
 *
 * Sending "this one goes up" would leave the server applying it to a list the
 * reader was not looking at; sending the list they *are* looking at is the
 * order they meant. One scope at a time: the organisation's templates and
 * somebody's own are arranged by different people, and the API refuses ids
 * that are not in the named list.
 */
export async function reorderMeetingTemplates(
  userId: string,
  scope: MeetingTemplateScope,
  ids: string[],
): Promise<void> {
  await apiFetch(`${collection(userId)}/actions/reorder`, {
    method: 'POST',
    body: JSON.stringify({ scope, ids }),
  });
}
